import React, { useState } from 'react';
import { Icon } from '../icons';
import HelpTip from './HelpTip';

const api = (path, opts = {}) =>
  fetch(path, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...opts });

// Icona bianca dentro il chip colorato: la regola generale ".card-head h2
// .icon" (arancio) ha specificita' piu' alta di una singola classe e
// vincerebbe sul colore ereditato, quindi si forza via style inline.
const CHIP_ICON_STYLE = { color: '#fff' };

function AppearanceCard({ theme, onSetTheme, sidebarCollapsed, onSetSidebarCollapsed }) {
  return (
    <div className="card">
      <div className="card-head">
        <h2>
          <span className="settings-icon accent"><Icon name="sliders" size={16} style={CHIP_ICON_STYLE} /></span>
          Aspetto
          <HelpTip text="Preferenze legate al tuo account: restano le stesse su ogni dispositivo o browser da cui accedi." />
        </h2>
      </div>

      <div className="field">
        <label>Tema</label>
        <div className="segmented">
          <div className={`segmented-thumb ${theme === 'dark' ? 'pos-1' : ''}`} />
          <button type="button" className={`segmented-option ${theme === 'light' ? 'active' : ''}`} onClick={() => onSetTheme('light')}>
            <Icon name="sun" size={15} />Chiaro
          </button>
          <button type="button" className={`segmented-option ${theme === 'dark' ? 'active' : ''}`} onClick={() => onSetTheme('dark')}>
            <Icon name="moon" size={15} />Scuro
          </button>
        </div>
      </div>

      <div className="field">
        <label>Menu laterale</label>
        <div className="segmented">
          <div className={`segmented-thumb ${sidebarCollapsed ? 'pos-1' : ''}`} />
          <button type="button" className={`segmented-option ${!sidebarCollapsed ? 'active' : ''}`} onClick={() => onSetSidebarCollapsed(false)}>
            Espanso
          </button>
          <button type="button" className={`segmented-option ${sidebarCollapsed ? 'active' : ''}`} onClick={() => onSetSidebarCollapsed(true)}>
            Compresso
          </button>
        </div>
      </div>
    </div>
  );
}

// Riusa /api/auth/change-password, lo stesso endpoint del flusso obbligatorio
// al primo accesso (ChangePassword.js) — qui pero' e' un'azione volontaria,
// niente redirect ne' logica di "forced": solo il form e un esito inline.
// Il mostra/nascondi per campo riusa lo stesso pattern di Login.js
// (.input-with-action + icone eye/eyeOff), un toggle indipendente per campo.
function PasswordCard({ user }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);

  // Gli stessi 3 requisiti applicati da POST /api/auth/change-password
  // (server/routes/auth.js), riscontrati qui dal vivo invece che scoperti
  // solo al submit tramite il messaggio di errore del server.
  const lengthOk = next.length >= 8;
  const notUsername = next.length > 0 && next !== (user && user.username);
  const differsFromCurrent = next.length > 0 && current.length > 0 && next !== current;
  const matchOk = confirm.length > 0 && next === confirm;

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (next !== confirm) { setMsg({ type: 'error', text: 'La conferma non coincide con la nuova password' }); return; }
    if (next.length < 8) { setMsg({ type: 'error', text: 'La nuova password deve avere almeno 8 caratteri' }); return; }
    setLoading(true);
    try {
      const res = await api('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      const data = await res.json();
      if (!res.ok) { setMsg({ type: 'error', text: data.error || 'Errore' }); return; }
      setCurrent(''); setNext(''); setConfirm('');
      setMsg({ type: 'success', text: 'Password aggiornata' });
    } catch {
      setMsg({ type: 'error', text: 'Impossibile contattare il server' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card" style={{ marginTop: '1.25rem' }}>
      <div className="card-head">
        <h2>
          <span className="settings-icon brand"><Icon name="key" size={16} style={CHIP_ICON_STYLE} /></span>
          Sicurezza
          <HelpTip text="Aggiorna la password del tuo account quando vuoi, non solo al primo accesso. Deve avere almeno 8 caratteri, essere diversa dal tuo username e dalla password attuale." />
        </h2>
      </div>
      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="set-cur-pw">Password attuale</label>
          <div className="input-with-action">
            <input id="set-cur-pw" className="input" type={showCurrent ? 'text' : 'password'} autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
            <button type="button" className="input-action" onClick={() => setShowCurrent((v) => !v)} aria-label={showCurrent ? 'Nascondi password' : 'Mostra password'}>
              <Icon name={showCurrent ? 'eyeOff' : 'eye'} size={16} />
            </button>
          </div>
        </div>
        <div className="field">
          <label htmlFor="set-new-pw">Nuova password</label>
          <div className="input-with-action">
            <input id="set-new-pw" className="input" type={showNew ? 'text' : 'password'} autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required />
            <button type="button" className="input-action" onClick={() => setShowNew((v) => !v)} aria-label={showNew ? 'Nascondi password' : 'Mostra password'}>
              <Icon name={showNew ? 'eyeOff' : 'eye'} size={16} />
            </button>
          </div>
          <div className="pw-requirements">
            <span className={`pw-requirement ${lengthOk ? 'met' : ''}`}><Icon name="check" size={12} />Almeno 8 caratteri</span>
            <span className="pw-requirement-sep">|</span>
            <span className={`pw-requirement ${notUsername ? 'met' : ''}`}><Icon name="check" size={12} />Diversa dallo username</span>
            <span className="pw-requirement-sep">|</span>
            <span className={`pw-requirement ${differsFromCurrent ? 'met' : ''}`}><Icon name="check" size={12} />Diversa dalla password attuale</span>
          </div>
        </div>
        <div className="field">
          <label htmlFor="set-cnf-pw">Conferma nuova password</label>
          <div className="input-with-action">
            <input id="set-cnf-pw" className="input" type={showConfirm ? 'text' : 'password'} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
            <button type="button" className="input-action" onClick={() => setShowConfirm((v) => !v)} aria-label={showConfirm ? 'Nascondi password' : 'Mostra password'}>
              <Icon name={showConfirm ? 'eyeOff' : 'eye'} size={16} />
            </button>
          </div>
          {confirm.length > 0 && (
            <span className={`pw-requirement ${matchOk ? 'met' : ''}`} style={{ marginTop: '0.4rem' }}>
              <Icon name={matchOk ? 'check' : 'x'} size={12} />{matchOk ? 'Corrisponde alla nuova password' : 'Non corrisponde alla nuova password'}
            </span>
          )}
        </div>
        {msg && <div className={`msg msg-${msg.type}`} style={{ marginBottom: '1rem' }}><Icon name={msg.type === 'success' ? 'check' : 'alert'} size={16} />{msg.text}</div>}
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Aggiornamento…' : 'Aggiorna password'}
        </button>
      </form>
    </div>
  );
}

// Categorie per ruolo: ogni voce raggruppa uno o piu' `type` interni (vedi
// server/lib/notify.js) che l'utente vede e silenzia come UN'unica cosa —
// license_expiring/license_expired sono lo stesso evento vissuto dall'utente
// ("le mie scadenze"), non due preferenze distinte.
const NOTIFICATION_CATEGORIES_BY_ROLE = {
  admin: [
    { types: ['support_report'], label: 'Nuove segnalazioni dai tecnici' },
    { types: ['reactivation_request'], label: 'Richieste di riattivazione' },
    { types: ['license_expiring', 'license_expired'], label: 'Licenze in scadenza o scadute' },
    { types: ['activation_code_created'], label: 'Codici attivazione creati da altri' },
    { types: ['version_published'], label: 'Nuove versioni da testare' },
    { types: ['version_approved'], label: 'Versioni approvate' },
  ],
  tecnico: [
    { types: ['support_report_closed'], label: 'Esito delle tue segnalazioni' },
  ],
};

function roleKey(role) {
  return role === 'amministratore' || role === 'superadmin' ? 'admin' : role;
}

function NotificationsCard({ user, onSetMutedTypes }) {
  const categories = NOTIFICATION_CATEGORIES_BY_ROLE[roleKey(user.role)] || [];
  if (!categories.length) return null; // es. sviluppatore: nessuna notifica da configurare

  const muted = user.muted_notification_types || [];
  const isEnabled = (cat) => !cat.types.some((t) => muted.includes(t));
  const toggle = (cat) => {
    const next = isEnabled(cat)
      ? [...muted, ...cat.types.filter((t) => !muted.includes(t))]
      : muted.filter((t) => !cat.types.includes(t));
    onSetMutedTypes(next);
  };

  return (
    <div className="card" style={{ marginTop: '1.25rem' }}>
      <div className="card-head">
        <h2>
          <span className="settings-icon accent"><Icon name="bell" size={16} style={CHIP_ICON_STYLE} /></span>
          Notifiche
          <HelpTip text="Scegli quali notifiche ricevere nel campanello. Disattivarne una non nasconde ne' cancella l'elemento corrispondente nella sua sezione, solo l'avviso." />
        </h2>
      </div>
      {categories.map((cat) => (
        <label key={cat.types[0]} className="settings-toggle-row">
          <span>{cat.label}</span>
          <span className="toggle-switch">
            <input type="checkbox" checked={isEnabled(cat)} onChange={() => toggle(cat)} />
            <span className="toggle-switch-track" />
            <span className="toggle-switch-thumb" />
          </span>
        </label>
      ))}
    </div>
  );
}

function Settings({ user, theme, onSetTheme, sidebarCollapsed, onSetSidebarCollapsed, onSetMutedTypes }) {
  return (
    <>
      <AppearanceCard theme={theme} onSetTheme={onSetTheme} sidebarCollapsed={sidebarCollapsed} onSetSidebarCollapsed={onSetSidebarCollapsed} />
      <PasswordCard user={user} />
      <NotificationsCard user={user} onSetMutedTypes={onSetMutedTypes} />
    </>
  );
}

export default Settings;
